import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/config";
import { 
  Shield, Calendar, Weight, Fingerprint, Syringe, FileText,
  Dog, Cat, Bird, Rabbit, PawPrint, CheckCircle, Clock, AlertCircle,
  User, AlertTriangle
} from "lucide-react";

const speciesIcons = { dog: Dog, cat: Cat, bird: Bird, rabbit: Rabbit, other: PawPrint };
const speciesLabels = { dog: "Cane", cat: "Gatto", bird: "Uccello", rabbit: "Coniglio", other: "Altro" };

export default function SharedPetView() {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSharedData();
  }, [shareToken]);

  const fetchSharedData = async () => {
    try {
      const response = await fetch(`${API}/shared/${shareToken}`);
      if (response.ok) {
        setData(await response.json());
      } else {
        const err = await response.json();
        setError(err.detail || "Link non valido");
      }
    } catch (error) {
      setError("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  const getDefaultPetImage = (species) => {
    const images = {
      dog: "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=400&q=80",
      cat: "https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=400&q=80",
      other: "https://images.unsplash.com/photo-1425082661705-1834bfd09dca?w=400&q=80"
    };
    return images[species] || images.other;
  };

  const getVaccineStatus = (expiryDate) => {
    if (!expiryDate) return 'valid';
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    if (daysUntilExpiry < 0) return 'expired';
    if (daysUntilExpiry <= 30) return 'expiring';
    return 'valid';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="animate-pulse">
          <Shield size={48} className="text-blue-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-100 px-6">
        <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mb-6">
          <AlertTriangle className="text-red-500" size={40} />
        </div>
        <h1 className="text-2xl font-bold text-slate-800 mb-2">Link non valido</h1>
        <p className="text-slate-500 text-center max-w-sm">
          {error === "Link scaduto" 
            ? "Questo link è scaduto. Chiedi al proprietario di generarne uno nuovo."
            : "Il link che stai cercando non esiste o è stato rimosso."}
        </p>
      </div>
    );
  }

  const { pet, owner, vaccines, documents } = data;
  const SpeciesIcon = speciesIcons[pet.species] || PawPrint;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="shared-pet-view">
      {/* Header Banner */}
      <div className="bg-blue-600 text-white p-4 text-center">
        <div className="flex items-center justify-center gap-2">
          <Shield size={18} />
          <span className="text-sm font-medium">
            Documento condiviso tramite Zampa
          </span>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          
          {/* Pet Header */}
          <div className="bg-gradient-to-r from-slate-700 to-slate-800 text-white p-6">
            <div className="flex items-center gap-5">
              <div className="w-20 h-20 rounded-2xl overflow-hidden border-2 border-white/30 flex-shrink-0">
                <img 
                  src={pet.photo_url || getDefaultPetImage(pet.species)}
                  alt={pet.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="text-slate-300 text-xs uppercase tracking-wide mb-1">Paziente</p>
                <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {pet.name}
                </h1>
                <div className="flex items-center gap-2 text-slate-300 text-sm">
                  <SpeciesIcon size={14} />
                  <span>{speciesLabels[pet.species]}</span>
                  {pet.breed && <span>• {pet.breed}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Pet Info */}
          <div className="p-6 border-b border-slate-200">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">
              Dati Anagrafici
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {pet.birth_date && (
                <div className="bg-slate-50 rounded-xl p-3">
                  <div className="flex items-center gap-2 text-slate-500 mb-1">
                    <Calendar size={14} />
                    <span className="text-xs">Data di nascita</span>
                  </div>
                  <p className="font-semibold text-slate-800">{pet.birth_date}</p>
                </div>
              )}
              {pet.weight && (
                <div className="bg-slate-50 rounded-xl p-3">
                  <div className="flex items-center gap-2 text-slate-500 mb-1">
                    <Weight size={14} />
                    <span className="text-xs">Peso</span>
                  </div>
                  <p className="font-semibold text-slate-800">{pet.weight} kg</p>
                </div>
              )}
              {pet.microchip && (
                <div className="bg-slate-50 rounded-xl p-3 col-span-2">
                  <div className="flex items-center gap-2 text-slate-500 mb-1">
                    <Fingerprint size={14} />
                    <span className="text-xs">Microchip</span>
                  </div>
                  <p className="font-mono font-semibold text-slate-800">{pet.microchip}</p>
                </div>
              )}
            </div>
          </div>

          {/* Owner */}
          <div className="p-6 border-b border-slate-200 bg-slate-50">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
              Proprietario
            </h3>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <User className="text-blue-600" size={18} />
              </div>
              <div>
                <p className="font-semibold text-slate-800">{owner.name}</p>
                <p className="text-sm text-slate-500">{owner.email}</p>
              </div>
            </div>
          </div>

          {/* Vaccines */}
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-2">
                <Syringe size={16} className="text-emerald-600" />
                Storico Vaccinale
              </h3>
              <span className="text-xs bg-slate-100 px-2 py-1 rounded-full text-slate-600">
                {vaccines.length} vaccin{vaccines.length !== 1 ? 'i' : 'o'}
              </span>
            </div>
            
            {vaccines.length === 0 ? (
              <p className="text-slate-400 text-sm italic">Nessun vaccino registrato</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 text-slate-500 font-medium">Vaccino</th>
                      <th className="text-left py-2 text-slate-500 font-medium">Data</th>
                      <th className="text-left py-2 text-slate-500 font-medium">Scadenza</th>
                      <th className="text-left py-2 text-slate-500 font-medium">Stato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vaccines.map((vaccine, index) => {
                      const status = getVaccineStatus(vaccine.expiry_date);
                      return (
                        <tr key={vaccine.vaccine_id || index} className="border-b border-slate-100">
                          <td className="py-3 font-medium text-slate-800">{vaccine.name}</td>
                          <td className="py-3 text-slate-600">{vaccine.date_administered}</td>
                          <td className="py-3 text-slate-600">{vaccine.expiry_date || '-'}</td>
                          <td className="py-3">
                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
                              status === 'valid' ? 'bg-emerald-100 text-emerald-700' :
                              status === 'expiring' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {status === 'valid' && <CheckCircle size={12} />}
                              {status === 'expiring' && <Clock size={12} />}
                              {status === 'expired' && <AlertCircle size={12} />}
                              {status === 'valid' && 'Valido'}
                              {status === 'expiring' && 'In scadenza'}
                              {status === 'expired' && 'Scaduto'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Documents */}
          {documents.length > 0 && (
            <div className="p-6">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-2 mb-4">
                <FileText size={16} className="text-blue-600" />
                Documenti allegati
              </h3>
              <div className="space-y-2">
                {documents.map((doc, index) => (
                  <div key={doc.document_id || index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                    <FileText className="text-blue-500" size={18} />
                    <span className="font-medium text-slate-800">{doc.title}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="p-4 bg-slate-100 text-center">
            <p className="text-xs text-slate-500">
              Visualizzato il {new Date().toLocaleDateString('it-IT')} • Powered by <strong>Zampa</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
